import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllUserTasksApi,
  getTaskApi,
  createTaskForChannelApi,
  executeTaskApi,
  getTaskOptionsApi,
  addTaskReplyAPI,
  updateTaskApi,
  deleteActionApi,
  deleteTaskApi,
  getTeamMemberTasks,
  getDashboardSummaryApi,
} from "./taskAPI";

export const getAllUserTasks = createAsyncThunk(
  "Task/getAllUserTasks",
  async (category, { rejectWithValue }) => {
    try {
      return await getAllUserTasksApi(category);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch all user tasks",
      );
    }
  },
);

export const fetchAllTeamMemberTask = createAsyncThunk(
  "Task/getAllTeamMemberTasks",
  async (_, { rejectWithValue }) => {
    try {
      return await getTeamMemberTasks();
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch team member tasks",
      );
    }
  },
);

export const getTask = createAsyncThunk(
  "Task/getTask",
  async (taskId, { rejectWithValue }) => {
    try {
      return await getTaskApi(taskId);
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to get task");
    }
  },
);

export const createTaskForChannel = createAsyncThunk(
  "Task/createTaskForChannel",
  async ({ taskUuid, taskData }, { rejectWithValue }) => {
    try {
      return await createTaskForChannelApi(taskUuid, taskData);
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to create task");
    }
  },
);

export const executeTask = createAsyncThunk(
  "Task/executeTask",
  async ({ taskData }, thunkAPI) => {
    try {
      return await executeTaskApi(taskData);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getTaskOptions = createAsyncThunk(
  "task/getTaskOptions",
  async ({ settingId, platformType, meetingId }, { rejectWithValue }) => {
    const key = `${platformType}_${settingId}`;

    try {
      const data = await getTaskOptionsApi(settingId, platformType, meetingId);
      return { key, data };
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed");
    }
  },
);

export const addTaskReply = createAsyncThunk(
  "task/addTaskReply",
  async ({ taskId, actionId, reply }, thunkAPI) => {
    try {
      const res = await addTaskReplyAPI({ taskId, actionId, reply });
      return res;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? { message: error.message },
      );
    }
  },
);

export const updateTask = createAsyncThunk(
  "Task/updateTaskStatus",
  async (payload, { rejectWithValue }) => {
    try {
      return await updateTaskApi(payload);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to update task status",
      );
    }
  },
);

export const deleteAction = createAsyncThunk(
  "task/deleteAction",
  async ({ taskId, actionUuid }, thunkAPI) => {
    try {
      const res = await deleteActionApi({ taskId, actionUuid });
      return res?.data ?? res;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? { message: error.message },
      );
    }
  },
);

export const deleteTask = createAsyncThunk(
  "task/deleteTask",
  async (taskId, thunkAPI) => {
    try {
      const res = await deleteTaskApi(taskId);
      return { taskId, data: res?.data ?? res };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? { message: error.message },
      );
    }
  },
);

export const getDashboardSummary = createAsyncThunk(
  "Task/getDashboardSummary",
  async (_, { rejectWithValue }) => {
    try {
      return await getDashboardSummaryApi();
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch dashboard summary",
      );
    }
  },
);

const tasksSlice = createSlice({
  name: "task",
  initialState: {
    task: null,
    loading: false,
    error: null,
    taskOptions: {},
    allUserTasks: [],
    dashboardSummary: null,
    dashboardLoading: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllUserTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllUserTasks.fulfilled, (state, action) => {
        state.allUserTasks = action.payload;
        state.loading = false;
      })
      .addCase(getAllUserTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTask.fulfilled, (state, action) => {
        state.task = action.payload;
        state.loading = false;
      })
      .addCase(getTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createTaskForChannel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTaskForChannel.fulfilled, (state, action) => {
        state.task = action.payload;
        state.loading = false;
      })
      .addCase(createTaskForChannel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(executeTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(executeTask.fulfilled, (state, action) => {
        state.loading = false;

        const updatedTask = action.payload?.task;
        if (!updatedTask || !state.task?.actions) return;

        const task = state.task.actions.find(
          (t) => t.uuid === updatedTask.uuid,
        );

        if (task) {
          Object.assign(task, updatedTask);
        }
      })
      .addCase(executeTask.rejected, (state, action) => {
        state.loading = false;

        const taskData = action.meta?.arg?.taskData;
        if (!taskData || !state.task?.actions) return;

        const { uuid } = taskData;
        const task = state.task.actions.find((t) => t.uuid === uuid);

        if (task) {
          task.status = "failed";
        }

        state.error = action.payload;
      })

      .addCase(getTaskOptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTaskOptions.fulfilled, (state, action) => {
        const { key, data } = action.payload;

        state.taskOptions = {
          ...state.taskOptions,
          [key]: data,
        };
      })
      .addCase(getTaskOptions.rejected, (state, action) => {
        state.error = action.payload || "Failed to fetch synced options";
      })

      .addCase(addTaskReply.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addTaskReply.fulfilled, (state, action) => {
        state.loading = false;

        const updatedAction = action.payload?.action;
        const { actionId } = action.meta?.arg || {};
        const targetUuid = updatedAction?.uuid || actionId;

        if (targetUuid && Array.isArray(state.task?.actions) && updatedAction) {
          state.task = {
            ...state.task,
            actions: state.task.actions.map((t) =>
              t.uuid === targetUuid
                ? {
                    ...t,
                    ...(typeof updatedAction === "object" ? updatedAction : {}),
                  }
                : t,
            ),
          };
        }
      })
      .addCase(addTaskReply.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        const incoming = action.payload;

        if (incoming?.actions && Array.isArray(incoming.actions)) {
          const changedAction = incoming.actions.find(
            (a) => a.uuid === action.meta.arg.uuid,
          );

          if (changedAction && Array.isArray(state.task?.actions)) {
            state.task = {
              ...state.task,
              actions: state.task.actions.map((t) =>
                t.uuid === changedAction.uuid ? changedAction : t,
              ),
            };
          } else {
            state.task = incoming;
          }
        }

        state.loading = false;
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteAction.fulfilled, (state, action) => {
        state.loading = false;
        state.task = action.payload;
      })
      .addCase(deleteAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteTask.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.loading = false;
        if (state.task?.uuid === action.payload.taskId) {
          state.task = null;
        }
      })
      .addCase(deleteTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchAllTeamMemberTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllTeamMemberTask.fulfilled, (state, action) => {
        state.allUserTasks = action.payload;
        state.loading = false;
      })
      .addCase(fetchAllTeamMemberTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getDashboardSummary.pending, (state) => {
        state.dashboardLoading = true;
        state.error = null;
      })
      .addCase(getDashboardSummary.fulfilled, (state, action) => {
        state.dashboardSummary = action.payload?.data ?? action.payload;
        state.dashboardLoading = false;
      })
      .addCase(getDashboardSummary.rejected, (state, action) => {
        state.dashboardLoading = false;
        state.error = action.payload;
      });
  },
});

export default tasksSlice.reducer;
